import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import { useAuth } from "../lib/auth";
import { ShoppingCart, Plus, Boxes, TrendingDown, ArrowRight, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function BranchDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pos, setPos] = useState([]);
  const [inv, setInv] = useState([]);
  const [branches, setBranches] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
    api.get("/inventory").then((r) => setInv(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/purchase-orders/pending-receipt/list").then((r) => setPendingReceipts(r.data)).catch(() => {});
  }, []);

  const branchName = branches.find((b) => b.id === user?.branch_id)?.name || t("dashboard.yourBranch");
  const pending = pos.filter((p) => p.status === "pending_approval").length;
  const approved = pos.filter((p) => p.status === "approved").length;
  const lowStock = inv.filter((i) => (i.quantity_on_hand || 0) < 20).length;
  const totalMonth = pos
    .filter((p) => new Date(p.created_at).getMonth() === new Date().getMonth())
    .reduce((a, b) => a + b.total, 0);

  return (
    <>
      <Topbar title={`${branchName}`} subtitle={t("dashboard.branchHello", { name: user?.full_name?.split(" ")[0] })} />
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="kpi-branch-pending" label={t("dashboard.kpis.branchPending")} value={pending} icon={ShoppingCart} accent={pending > 0} />
          <KpiCard testId="kpi-branch-approved" label={t("dashboard.kpis.branchApproved")} value={approved} icon={ShoppingCart} />
          <KpiCard testId="kpi-branch-month" label={t("dashboard.kpis.branchMonth")} value={fmtMXN(totalMonth)} icon={Boxes} />
          <KpiCard testId="kpi-branch-lowstock" label={t("dashboard.kpis.lowStock")} value={lowStock} icon={TrendingDown} />
        </div>

        <button
          onClick={() => navigate("/ocs/nueva")}
          data-testid="branch-create-po-btn"
          className="w-full bg-gradient-to-r from-[#FFDF00] to-[#FFC700] text-[#1A1A1A] rounded-2xl p-6 flex items-center justify-between hover:brightness-105 active:scale-[0.99] transition-all shadow-[0_4px_0_#D4B800]"
        >
          <div className="text-left">
            <div className="font-display font-extrabold text-2xl">{t("dashboard.createPo")}</div>
            <div className="text-sm font-medium opacity-80 mt-0.5">{t("dashboard.createPoSub")}</div>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-[#1A1A1A] flex items-center justify-center">
            <Plus className="w-7 h-7 text-[#FFDF00]" strokeWidth={3} />
          </div>
        </button>

        {pendingReceipts.length > 0 && (
          <motion.button
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate("/recepciones")}
            data-testid="branch-receptions-cta"
            className="w-full bg-white border-2 border-amber-300 rounded-2xl p-5 flex items-center justify-between hover:border-amber-400 transition-all text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                <Truck className="w-6 h-6" />
              </div>
              <div>
                <div className="font-display font-extrabold text-lg text-[#1A1A1A]">
                  {t("dashboard.pendingReceptions", { count: pendingReceipts.length })}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{t("dashboard.pendingReceptionsSub")}</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-700" />
          </motion.button>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 pb-3 flex items-center justify-between">
            <h2 className="font-display font-bold text-lg">{t("dashboard.myRecentOrders")}</h2>
            <button onClick={() => navigate("/ocs")} className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1">
              {t("common.viewAll")} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {pos.length === 0 ? (
            <div className="p-6"><EmptyState title={t("dashboard.noOrdersTitle")} message={t("dashboard.noOrdersMsg")} /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supplier")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.slice(0, 8).map((po, idx) => (
                    <motion.tr
                      key={po.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => navigate(`/ocs/${po.id}`)}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="px-6 py-3.5 font-mono text-sm font-semibold">{po.folio}</td>
                      <td className="px-6 py-3.5 text-sm text-slate-700">{po.supplier_name}</td>
                      <td className="px-6 py-3.5 text-sm font-semibold text-right">{fmtMXN(po.total)}</td>
                      <td className="px-6 py-3.5"><StatusBadge status={po.status} /></td>
                      <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(po.created_at)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
